import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiUrl, getAuthHeaders } from '../../config/api.config';

const initialState = {
  requests: [],
  members: [],
  statistics: null,
  loading: false,
  error: null,
  success: false,
  submitSuccess: false
};

// Submit ministry request (Public)
export const submitMinistryRequest = createAsyncThunk(
  'ministry/submitRequest',
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        getApiUrl('/ministry/requests'),
        requestData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to submit request'
      );
    }
  }
);

// Get all ministry requests (Admin)
export const getAllRequests = createAsyncThunk(
  'ministry/getAllRequests',
  async ({ status, ministry } = {}, { rejectWithValue }) => {
    try {
      let url = '/ministry/requests';
      const params = [];
      if (status) params.push(`status=${status}`);
      if (ministry) params.push(`ministry=${ministry}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(getApiUrl(url), {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch requests'
      );
    }
  }
);

// Approve ministry request (Admin)
export const approveRequest = createAsyncThunk(
  'ministry/approveRequest',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        getApiUrl(`/ministry/requests/${id}/approve`),
        {},
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to approve request'
      );
    }
  }
);

// Decline ministry request (Admin)
export const declineRequest = createAsyncThunk(
  'ministry/declineRequest',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        getApiUrl(`/ministry/requests/${id}/decline`),
        { reason },
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to decline request'
      );
    }
  }
);

// Get all ministry members (Admin)
export const getAllMembers = createAsyncThunk(
  'ministry/getAllMembers',
  async ({ ministry, status } = {}, { rejectWithValue }) => {
    try {
      let url = '/ministry/members';
      const params = [];
      if (ministry) params.push(`ministry=${ministry}`);
      if (status) params.push(`status=${status}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(getApiUrl(url), {
        headers: getAuthHeaders()
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch members'
      );
    }
  }
);

// Remove ministry member (Admin)
export const removeMember = createAsyncThunk(
  'ministry/removeMember',
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(
        getApiUrl(`/ministry/members/${id}`),
        { headers: getAuthHeaders() }
      );
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to remove member'
      );
    }
  }
);

// Get ministry statistics (Admin)
export const getStatistics = createAsyncThunk(
  'ministry/getStatistics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        getApiUrl('/ministry/statistics'),
        { headers: getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch statistics'
      );
    }
  }
);

const ministrySlice = createSlice({
  name: 'ministry',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    clearSubmitSuccess: (state) => {
      state.submitSuccess = false;
    }
  },
  extraReducers: (builder) => {
    builder
      // Submit request
      .addCase(submitMinistryRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitMinistryRequest.fulfilled, (state) => {
        state.loading = false;
        state.submitSuccess = true;
      })
      .addCase(submitMinistryRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get all requests
      .addCase(getAllRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload.data.requests;
      })
      .addCase(getAllRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Approve request
      .addCase(approveRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveRequest.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(r => r._id === action.payload.data.request._id);
        if (index !== -1) {
          state.requests[index] = action.payload.data.request;
        }
        state.success = true;
      })
      .addCase(approveRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Decline request
      .addCase(declineRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(declineRequest.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.requests.findIndex(r => r._id === action.payload.data.request._id);
        if (index !== -1) {
          state.requests[index] = action.payload.data.request;
        }
        state.success = true;
      })
      .addCase(declineRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get all members
      .addCase(getAllMembers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllMembers.fulfilled, (state, action) => {
        state.loading = false;
        state.members = action.payload.data.members;
      })
      .addCase(getAllMembers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Remove member
      .addCase(removeMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeMember.fulfilled, (state, action) => {
        state.loading = false;
        state.members = state.members.filter(m => m._id !== action.payload);
        state.success = true;
      })
      .addCase(removeMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get statistics
      .addCase(getStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.statistics = action.payload.data;
      })
      .addCase(getStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearSuccess, clearSubmitSuccess } = ministrySlice.actions;
export default ministrySlice.reducer;
